import { getCookie, isBrowser, setCookie } from "./browser";
import type { AnalyticsUtm } from "./events";
import { cleanUtmField } from "./utmValue";

export const ATTRIBUTION_COOKIE = "ml_attribution";
export const ATTRIBUTION_MAX_AGE_SECONDS = 60 * 60 * 24 * 365;

export type FirstTouchAttribution = AnalyticsUtm;

function readCurrentUtm(search: string): AnalyticsUtm {
  const params = new URLSearchParams(search);
  return {
    source: cleanUtmField(params.get("utm_source")),
    medium: cleanUtmField(params.get("utm_medium")),
    campaign: cleanUtmField(params.get("utm_campaign")),
    content: cleanUtmField(params.get("utm_content")),
    term: cleanUtmField(params.get("utm_term")),
  };
}

function parseStored(raw: string | null): FirstTouchAttribution | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as FirstTouchAttribution;
    if (!parsed || typeof parsed !== "object") return null;
    return {
      source: cleanUtmField(parsed.source),
      medium: cleanUtmField(parsed.medium),
      campaign: cleanUtmField(parsed.campaign),
      content: cleanUtmField(parsed.content),
      term: cleanUtmField(parsed.term),
    };
  } catch {
    return null;
  }
}

/**
 * Capture first-touch UTM once. Later navigations do not overwrite.
 */
export function captureFirstTouchAttribution(): FirstTouchAttribution {
  if (!isBrowser()) return {};

  const existing = parseStored(getCookie(ATTRIBUTION_COOKIE));
  if (existing) return existing;

  const attribution = readCurrentUtm(window.location.search);
  setCookie(
    ATTRIBUTION_COOKIE,
    JSON.stringify(attribution),
    ATTRIBUTION_MAX_AGE_SECONDS,
  );
  return attribution;
}

export function getFirstTouchAttribution(): FirstTouchAttribution {
  if (!isBrowser()) return {};
  return parseStored(getCookie(ATTRIBUTION_COOKIE)) ?? captureFirstTouchAttribution();
}

export function toUtmPayload(attribution: FirstTouchAttribution): AnalyticsUtm {
  return {
    source: cleanUtmField(attribution.source) ?? null,
    medium: cleanUtmField(attribution.medium) ?? null,
    campaign: cleanUtmField(attribution.campaign) ?? null,
    content: cleanUtmField(attribution.content) ?? null,
    term: cleanUtmField(attribution.term) ?? null,
  };
}
